/**
 * The one module that knows Amplitude exists.
 *
 * Everything the storefront measures goes through `track()` here, for the same
 * reason `pricing.py` is the only place that knows what a dish costs: a vocabulary
 * spread across twenty screens stops being a vocabulary. `EVENTS` is the closed
 * list, and a new step means a line in it rather than a string invented at a call
 * site — a typo'd event name does not fail, it just quietly never appears in a
 * funnel nobody thinks to doubt.
 *
 * What this is for: the restaurant's own dashboard answers the restaurant's
 * questions off MongoDB, and it stays the source of truth for money. This answers
 * ours — where a guest leaves between the scan and the cart, which goal converts,
 * whether the third plate is worth its tokens.
 *
 * What never leaves for Amplitude: a guest's allergies, the free text they typed
 * at the model, their name, their email, and the body figures on their profile.
 * The first is health information and the second is their own words; both are
 * covered by a sentence at /privacy that this file has to keep true. `scrub()`
 * enforces it rather than trusting every future call site to remember.
 */
import { guestId } from "@/lib/api";

// CRA inlines this at build time. No key is a legitimate state — dev, a fork, a
// build somebody made without one — and it means silence, never a crash.
const API_KEY = process.env.REACT_APP_AMPLITUDE_API_KEY;

/**
 * Every event the storefront may send. Server-side `order_placed` is deliberately
 * absent: the server prices the order, so the server reports it, and a client
 * copy would only be a second number to reconcile.
 */
export const EVENTS = Object.freeze({
  // Getting to the table
  QR_SCANNED: "qr_scanned",
  SCAN_REQUIRED_SHOWN: "scan_required_shown",
  ORDER_STARTED: "order_started",
  STEP_VIEWED: "step_viewed",
  MODE_SELECTED: "mode_selected",

  // Auto Plate
  AI_OPENED: "ai_opened",
  QUIZ_ANSWERED: "quiz_answered",
  AI_WISH_SUBMITTED: "ai_wish_submitted",
  AI_PLATES_SHOWN: "ai_plates_shown",
  AI_PLATES_FAILED: "ai_plates_failed",
  AI_PLATE_ACCEPTED: "ai_plate_accepted",
  AI_PLATE_ITEM_REMOVED: "ai_plate_item_removed",
  AI_TRY_ANOTHER: "ai_try_another",

  // Cart and order
  CART_ITEM_ADDED: "cart_item_added",
  CART_ITEM_REMOVED: "cart_item_removed",
  CART_CLEARED: "cart_cleared",
  ORDER_SUBMITTED: "order_submitted",
  ORDER_FAILED: "order_failed",
  PAYMENT_COMPLETED: "payment_completed",
  ORDER_REORDERED: "order_reordered",

  // Account
  SIGNED_IN: "signed_in",
  SIGNED_UP: "signed_up",
  SIGNED_OUT: "signed_out",
  PROFILE_SAVED: "profile_saved",
  LANGUAGE_CHANGED: "language_changed",
});

/**
 * Property names that may never reach Amplitude, whatever a call site passes.
 *
 * A denylist is a backstop, not the design — the design is that call sites send
 * counts and categories. It exists because the cost of one careless spread of a
 * guest object is somebody's medical information sitting in a third-party
 * warehouse, and that is not a mistake you find by reading a diff.
 */
const FORBIDDEN_PROPS = new Set([
  "allergy",
  "allergies",
  "allergy_note",
  "exclude_ingredients",
  "free_text",
  "wish",
  "text",
  "note",
  "name",
  "guest_name",
  "email",
  "age",
  "gender",
  "height_cm",
  "weight_kg",
]);

function scrub(props) {
  if (!props) return undefined;
  const out = {};
  for (const [key, value] of Object.entries(props)) {
    if (FORBIDDEN_PROPS.has(key)) {
      if (process.env.NODE_ENV === "development") {
        // Loud in dev, silent in production: the guest is not the audience for
        // our own mistake, but whoever added the call site is.
        console.warn(`[analytics] dropped forbidden property "${key}"`);
      }
      continue;
    }
    if (value !== undefined && value !== null) out[key] = value;
  }
  return out;
}

// Which restaurant the guest is in. Held here rather than passed at every call
// site so that autocaptured events carry it too — the enrichment plugin below is
// what actually attaches it. Accounts/groups are not on this plan, so tenancy
// lives in event properties.
const tenant = { restaurant_slug: null, restaurant_id: null };

let ready = null;

/** Attaches the tenant to every event, including the ones the SDK sends itself. */
function tenantPlugin() {
  return {
    name: "fuelly-tenant",
    type: "enrichment",
    setup: async () => undefined,
    execute: async (event) => {
      if (tenant.restaurant_slug) {
        event.event_properties = { ...event.event_properties, ...tenant };
      }
      return event;
    },
  };
}

/**
 * Load and start the SDK. Idempotent, and safe to call from anywhere.
 *
 * The init promise is the thing `track()` waits on, because the SDK *drops*
 * calls made before it is initialised rather than queueing them — so the events
 * fired by the first render (a scan resolving, the welcome step) would be exactly
 * the ones that vanish.
 */
export function initAnalytics() {
  if (ready) return ready;
  if (!API_KEY) {
    ready = Promise.resolve(null);
    return ready;
  }
  ready = (async () => {
    const [amplitude, replay] = await Promise.all([
      // Dynamic, so the SDK and the replay recorder stay out of the first chunk:
      // a guest at a table waits for the menu, not for our measurement.
      import("@amplitude/analytics-browser"),
      import("@amplitude/plugin-session-replay-browser"),
    ]);

    amplitude.add(
      replay.sessionReplayPlugin({
        sampleRate: 1,
        privacyConfig: {
          // Every text node is masked, not only inputs. What a recording is for
          // here is where a thumb hesitated, never what the guest typed.
          defaultMaskLevel: "conservative",
          // Blocked outright, because masking is not the point: these areas hold
          // allergies, the guest's own words and their body figures, and a
          // blocked region is not recorded at all.
          blockSelector: [".amp-block"],
        },
      }),
    );
    amplitude.add(tenantPlugin());

    const device = guestId();
    await amplitude.init(API_KEY, {
      // The same id the API already knows this device by, which is what lets a
      // session here line up with the server's own `order_placed` and with
      // `funnel_events.guest_id` in MongoDB. "anonymous" is the storage-blocked
      // fallback and belongs to no one device, so the SDK picks its own instead.
      deviceId: device === "anonymous" ? undefined : device,
      autocapture: {
        attribution: true,
        pageViews: true,
        sessions: true,
        // Off on purpose. Element and form autocapture record the *text* of what
        // was clicked, which on this storefront includes allergy chips and a
        // guest's typed craving. Every interaction worth counting is in EVENTS
        // above, with properties we chose.
        elementInteractions: false,
        formInteractions: false,
        fileDownloads: false,
      },
      logLevel: 0,
    }).promise;

    return amplitude;
  })().catch(() => null); // a blocked or failed SDK is not an error a guest sees
  return ready;
}

/**
 * Record something the guest did. Fire and forget: never awaited, never throws.
 *
 * The same rule as the `/api/events` beacon it sits beside — our bookkeeping is
 * not allowed to be the reason somebody cannot order lunch.
 */
export function track(event, props) {
  initAnalytics()
    .then((amplitude) => amplitude && amplitude.track(event, scrub(props)))
    .catch(() => {});
}

/** Which restaurant this is. Called once the tenant document has loaded. */
export function setTenant(slug, restaurantId) {
  tenant.restaurant_slug = slug || null;
  tenant.restaurant_id = restaurantId || null;
}

/**
 * Bind the session to a signed-in guest.
 *
 * `user_id` is the account's own pseudonymous id — never the email address. The
 * difference matters: one lets us count a returning guest, the other hands a
 * third party a way to contact them.
 */
export function identify(user, extra) {
  if (!user?.user_id) return;
  initAnalytics()
    .then((amplitude) => {
      if (!amplitude) return;
      amplitude.setUserId(user.user_id);
      const identity = new amplitude.Identify();
      identity.set("has_profile", Boolean(user.goal));
      if (user.goal) identity.set("goal", user.goal);
      if (extra?.language) identity.set("language", extra.language);
      amplitude.identify(identity);
    })
    .catch(() => {});
}

/**
 * Forget who this is, on sign-out.
 *
 * Not `reset()`: that would also mint a new device id, and this is the same
 * device it was a moment ago — the phone did not change hands because somebody
 * signed out of it. The device id stays ours, from `guestId()`.
 */
export function resetIdentity() {
  initAnalytics()
    .then((amplitude) => amplitude && amplitude.setUserId(undefined))
    .catch(() => {});
}
